import { getBike, MY_BIKE_EVENT } from '@theme/my-bike';

/**
 * Voge search (<voge-search>): desktop header dropdown (data-mode="dropdown",
 * snippets/header-inline-search.liquid) and the mobile full-screen sheet (data-mode="sheet",
 * snippets/search-modal.liquid, opened by the header search icon via #search-modal/showDialog).
 *
 * - Empty field: suggestions (My bike collection, recent searches, top categories).
 * - Typing: predictive results from /search/suggest rendered by sections/voge-search-results.liquid.
 * - Keyboard: ↑/↓ move through the options (aria-activedescendant), Enter opens the highlighted
 *   option or submits the form to /search, Escape closes the dropdown (the sheet closes through
 *   Horizon's dialog). Clicking outside closes the dropdown.
 * - Submitted queries are remembered (localStorage 'voge:recent-searches', max. 5).
 * The form is a plain GET form to /search, so search works without JavaScript.
 */

const RECENT_KEY = 'voge:recent-searches';
const SECTION_ID = 'voge-search-results';
let uidCounter = 0;

/** @returns {Record<string, string>} */
function routes() {
  // @ts-ignore – `Theme` is a global declared in snippets/scripts.liquid
  return typeof Theme !== 'undefined' && Theme.routes ? Theme.routes : {};
}

/** @returns {string[]} */
function readRecent() {
  try {
    const value = JSON.parse(localStorage.getItem(RECENT_KEY) || '[]');
    return Array.isArray(value) ? value.filter((item) => typeof item === 'string').slice(0, 5) : [];
  } catch {
    return [];
  }
}

/** @param {string} term */
function rememberSearch(term) {
  const clean = term.trim();
  if (!clean) return;
  const list = [clean, ...readRecent().filter((item) => item.toLowerCase() !== clean.toLowerCase())].slice(0, 5);
  try {
    localStorage.setItem(RECENT_KEY, JSON.stringify(list));
  } catch {
    /* storage unavailable */
  }
}

class VogeSearch extends HTMLElement {
  #controller = new AbortController();
  /** @type {AbortController | null} */
  #fetchController = null;
  /** @type {number | undefined} */
  #timer;
  #term = '';
  #active = -1;
  #uid = `voge-search-${++uidCounter}`;

  connectedCallback() {
    const { signal } = this.#controller;
    this.input = /** @type {HTMLInputElement | null} */ (this.querySelector('[data-search-input]'));
    this.panel = /** @type {HTMLElement | null} */ (this.querySelector('[data-search-panel]'));
    this.slot = /** @type {HTMLElement | null} */ (this.querySelector('[data-search-results-slot]'));
    this.suggestions = /** @type {HTMLElement | null} */ (this.querySelector('[data-search-suggestions]'));
    this.form = this.querySelector('form');
    if (!this.input || !this.panel || !this.slot) return;

    this.input.addEventListener('input', this.#onInput, { signal });
    this.input.addEventListener('keydown', this.#onKeydown, { signal });
    this.input.addEventListener('focus', () => this.#open(), { signal });
    this.input.addEventListener('click', () => this.#open(), { signal });
    this.form?.addEventListener('submit', () => rememberSearch(this.input?.value || ''), { signal });
    this.panel.addEventListener('click', this.#onPanelClick, { signal });
    this.panel.addEventListener('mousemove', this.#onHover, { signal });
    document.addEventListener(MY_BIKE_EVENT, () => this.#renderSuggestions(), { signal });

    if (this.isDropdown) {
      document.addEventListener('pointerdown', this.#onOutside, { signal });
      this.addEventListener('focusout', this.#onFocusOut, { signal });
    } else {
      // Sheet: focus the field and refresh suggestions whenever the dialog opens
      const dialog = this.closest('dialog');
      if (dialog) {
        new MutationObserver(() => {
          if (dialog.open) {
            this.#renderSuggestions();
            requestAnimationFrame(() => this.input?.focus());
          }
        }).observe(dialog, { attributes: true, attributeFilter: ['open'] });
      }
    }
    this.#renderSuggestions();
  }

  disconnectedCallback() {
    this.#controller.abort();
    this.#fetchController?.abort();
  }

  get isDropdown() {
    return this.dataset.mode === 'dropdown';
  }

  #open() {
    if (!this.panel) return;
    this.#renderSuggestions();
    this.panel.hidden = false;
    this.input?.setAttribute('aria-expanded', 'true');
  }

  #close() {
    if (!this.panel || !this.isDropdown) return;
    this.panel.hidden = true;
    this.input?.setAttribute('aria-expanded', 'false');
    this.#setActive(-1);
  }

  /** Fills the My bike chip and the recent searches. */
  #renderSuggestions() {
    const box = this.suggestions;
    if (!box) return;
    const bike = getBike();
    const bikeLink = /** @type {HTMLAnchorElement | null} */ (box.querySelector('[data-search-bike]'));
    if (bikeLink) {
      bikeLink.hidden = !bike;
      if (bike) {
        bikeLink.href = bike.url;
        const label = bikeLink.querySelector('[data-search-bike-label]');
        if (label) label.textContent = (box.dataset.shopLabel || '[model]').replace('[model]', bike.title);
      }
    }
    const list = box.querySelector('[data-search-recent]');
    if (list) {
      list.querySelectorAll('[data-recent-item]').forEach((item) => item.remove());
      for (const term of readRecent()) {
        const li = document.createElement('li');
        li.dataset.recentItem = '';
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'voge-search__chip';
        button.dataset.searchOption = '';
        button.dataset.searchTerm = term;
        button.setAttribute('role', 'option');
        button.setAttribute('aria-selected', 'false');
        button.textContent = term;
        li.append(button);
        list.append(li);
      }
    }
    this.#indexOptions();
  }

  /** @param {Event} event */
  #onInput = () => {
    window.clearTimeout(this.#timer);
    this.#open();
    this.#timer = window.setTimeout(() => this.#search(), 180);
  };

  async #search() {
    const term = (this.input?.value || '').trim();
    if (!this.slot) return;
    if (term === this.#term) return;
    this.#term = term;
    this.#fetchController?.abort();

    if (!term) {
      this.slot.replaceChildren();
      if (this.suggestions) this.suggestions.hidden = false;
      this.#indexOptions();
      return;
    }

    const controller = new AbortController();
    this.#fetchController = controller;
    const url = new URL(routes().predictive_search_url || '/search/suggest', location.origin);
    url.searchParams.set('q', term);
    url.searchParams.set('resources[type]', 'product,collection,page');
    url.searchParams.set('resources[limit]', '6');
    url.searchParams.set('resources[limit_scope]', 'each');
    url.searchParams.set('resources[options][unavailable_products]', 'last');
    url.searchParams.set('section_id', SECTION_ID);
    this.setAttribute('aria-busy', 'true');
    try {
      const response = await fetch(url.toString(), { signal: controller.signal });
      if (!response.ok) throw new Error(String(response.status));
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const results = doc.querySelector('[data-search-results]');
      if (controller.signal.aborted || !results) return;
      this.slot.replaceChildren(results);
      if (this.suggestions) this.suggestions.hidden = true;
      this.#indexOptions();
    } catch (error) {
      if (/** @type {Error} */ (error).name !== 'AbortError') {
        this.slot.replaceChildren();
        if (this.suggestions) this.suggestions.hidden = false;
        this.#indexOptions();
      }
    } finally {
      if (this.#fetchController === controller) this.removeAttribute('aria-busy');
    }
  }

  /** @returns {HTMLElement[]} visible options in order */
  get #options() {
    if (!this.panel) return [];
    return /** @type {HTMLElement[]} */ (Array.from(this.panel.querySelectorAll('[data-search-option]'))).filter(
      (option) => !option.hidden && !option.closest('[hidden]')
    );
  }

  #indexOptions() {
    this.#options.forEach((option, index) => {
      option.id = `${this.#uid}-option-${index}`;
    });
    this.#setActive(-1);
  }

  /** @param {number} index */
  #setActive(index) {
    const options = this.#options;
    this.#active = index;
    options.forEach((option, i) => option.setAttribute('aria-selected', String(i === index)));
    const current = options[index];
    if (current) {
      this.input?.setAttribute('aria-activedescendant', current.id);
      current.scrollIntoView({ block: 'nearest' });
    } else {
      this.input?.removeAttribute('aria-activedescendant');
    }
  }

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    const options = this.#options;
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.#open();
        if (options.length) this.#setActive((this.#active + 1) % options.length);
        break;
      case 'ArrowUp':
        event.preventDefault();
        if (options.length) this.#setActive(this.#active <= 0 ? options.length - 1 : this.#active - 1);
        break;
      case 'Enter': {
        const current = options[this.#active];
        if (current) {
          event.preventDefault();
          this.#activate(current);
        } else {
          rememberSearch(this.input?.value || '');
        }
        break;
      }
      case 'Escape':
        if (this.isDropdown && this.panel && !this.panel.hidden) {
          event.preventDefault();
          event.stopPropagation();
          this.#close();
        }
        break;
      default:
        break;
    }
  };

  /** @param {HTMLElement} option */
  #activate(option) {
    const term = option.dataset.searchTerm;
    if (term !== undefined && this.input) {
      this.input.value = term;
      this.input.focus();
      this.#search();
      return;
    }
    if (this.input?.value) rememberSearch(this.input.value);
    if (option instanceof HTMLAnchorElement) option.click();
  }

  /** @param {MouseEvent} event */
  #onPanelClick = (event) => {
    const option = /** @type {HTMLElement} */ (event.target).closest('[data-search-option]');
    if (!(option instanceof HTMLElement)) return;
    if (option.dataset.searchTerm !== undefined) {
      event.preventDefault();
      this.#activate(option);
      return;
    }
    if (this.input?.value) rememberSearch(this.input.value);
  };

  /** @param {MouseEvent} event */
  #onHover = (event) => {
    const option = /** @type {HTMLElement} */ (event.target).closest('[data-search-option]');
    if (!(option instanceof HTMLElement)) return;
    const index = this.#options.indexOf(option);
    if (index !== this.#active) {
      this.#active = index;
      this.#options.forEach((item, i) => item.setAttribute('aria-selected', String(i === index)));
    }
  };

  /** @param {PointerEvent} event */
  #onOutside = (event) => {
    if (!this.contains(/** @type {Node} */ (event.target))) this.#close();
  };

  /** @param {FocusEvent} event */
  #onFocusOut = (event) => {
    const next = /** @type {Node | null} */ (event.relatedTarget);
    if (next && !this.contains(next)) this.#close();
  };
}

if (!customElements.get('voge-search')) {
  customElements.define('voge-search', VogeSearch);
}
